import React, { useState, useEffect, useRef } from 'react';

import { DialogUtility } from '@syncfusion/ej2-popups';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import {
    GridComponent,
    Inject,
    Page,
    Selection,
    DetailRow,
    Toolbar,
    Sort,
    ColumnChooser
} from '@syncfusion/ej2-react-grids';

import { RetryEnrollmentApi } from './RetryEnrollmentApi';
import { EnrollmentGridColumns, EnrollmentResult, selectionSettings, tabsToolbarOptions, TransactionEventsGridColumns } from './RetryFailedRuns.Metadata';


const RetryFailedRuns: React.FC = () => {
    // State declarations
    const [status, setStatus] = useState<string>('1');
    const [results, setResults] = useState<EnrollmentResult[]>([]);
    const [selectedEnrollments, setSelectedEnrollments] = useState<EnrollmentResult[]>([]);
    const [pageSettings] = useState({ pageCount: 5, pageSize: 5 });
    const requeueEnrollmentGridRef = useRef<GridComponent>(null);

    // When rows are selected or deselected, update the selectedEnrollments state
    const handleDataGridSelection = () => {
        if (requeueEnrollmentGridRef.current) {
            const selectedRows = requeueEnrollmentGridRef.current.getSelectedRecords() || [];
            setSelectedEnrollments(selectedRows as EnrollmentResult[]);
        }
    };

    // Toolbar click handler for requeueing enrollments
    const toolbarClick = () => {
        if (selectedEnrollments && selectedEnrollments.length > 0) {
            const confirm = DialogUtility.confirm({
                animationSettings: { effect: 'None' },
                isModal: true,
                content: '<b>Are you sure you want to requeue the selected enrollments to service bus?</b>',
                cancelButton: {
                    text: 'NO',
                    cssClass: 'e-cancel mt-2',
                    click: () => {
                        confirm.hide();
                    },
                },
                okButton: {
                    text: 'YES',
                    cssClass: 'e-submit mt-2',
                    click: async () => {
                        const data = selectedEnrollments.map(enrollment => ({
                            enrollmentID: enrollment.enrollmentID,
                            instanceId: enrollment.instanceId
                        }));
                        try {
                            await RetryEnrollmentApi.RequeueEnrollmentProcess(data);
                            handleSearch();
                            console.log("Requeue successful", data);
                        } catch (error) {
                            console.error("Error requeuing enrollments:", error);
                        }
                        confirm.hide();
                    },
                },
                showCloseIcon: true,
                position: { X: 'center', Y: 'center' },
            });
        } else {
            const alertDialog = DialogUtility.alert({
                animationSettings: { effect: 'None' },
                isModal: true,
                closeOnEscape: true,
                content: 'No enrollment was selected.',
                okButton: {
                    text: 'OK',
                    cssClass: 'e-flat e-info',
                    click: () => alertDialog.hide(),
                },
                showCloseIcon: false,
                position: { X: 'center', Y: 'center' },
            });
        }
    };

    // Load enrollments from the API
    const handleSearch = async () => {
        try {
            const params = { getLogsFor: status };
            const response = await RetryEnrollmentApi.LoadFailedEnrollments(params);
            setResults(response || []);
            // Clear any previously selected rows
            setSelectedEnrollments([]);
        } catch (error) {
            console.error('Error loading failed enrollments:', error);
        }
    };

    // Cancel search
    const handleCancel = () => {
        setStatus('1');
        setResults([]);
        setSelectedEnrollments([]);
        handleSearch();
    };

    // Row expansion detail template
    const detailTemplate = (rowData: EnrollmentResult) => (
        <div style={{ margin: '1rem' }}>
            <h4>Transaction History</h4>
            <GridComponent
                dataSource={rowData.transactionEvents}
                columns={TransactionEventsGridColumns}
                allowPaging={false}
                allowSorting={false}
            >
                <Inject services={[Page]} />
            </GridComponent>
        </div>
    );

    // On component mount, load data
    useEffect(() => {
        void handleSearch();
    }, []);

    return (
        <>
            <div className='row p-3'>
                <div className='col-md-2'>
                    <DropDownListComponent
                        dataSource={[
                            { text: 'Exceeded Retry', value: '1' },
                            { text: 'Error - Assigning Rep', value: '2' },
                        ]}
                        fields={{ text: 'text', value: 'value' }}
                        placeholder="Status"
                        value={status}
                        change={(e) => {
                            setStatus(e.value as string);
                        }}
                    />
                </div>
                <div className='col-md-4'>
                    <input
                        type='button'
                        className='btn btn-secondary customBtn py-2 me-2'
                        value='Clear'
                        style={{ backgroundColor: '#adb5bd' }}
                        onClick={handleCancel}
                    />
                    <input
                        type='submit'
                        className='btn btn-primary mx-2 py-2 px-2 allign-middle customBtn'
                        style={{ color: '#fff' }}
                        value='Search'
                        onClick={handleSearch}
                    />
                </div>
            </div>

            <div className='gridContainer'>

                <GridComponent
                    id="requeueGridRef"
                    dataSource={results}
                    ref={requeueEnrollmentGridRef}
                    allowSorting={true}
                    allowPaging={true}
                    pageSettings={pageSettings}
                    toolbar={tabsToolbarOptions as any}
                    showColumnChooser={true}
                    rowSelected={handleDataGridSelection}
                    rowDeselected={handleDataGridSelection}
                    toolbarClick={toolbarClick}
                    detailTemplate={detailTemplate}
                    selectionSettings={selectionSettings}
                    rowHeight={48}
                    columns={EnrollmentGridColumns}
                    allowTextWrap={true}
                >
                    <Inject services={[Page, Selection, Sort, Toolbar, ColumnChooser, DetailRow]} />
                </GridComponent>
            </div>
        </>
    );
};

export default RetryFailedRuns;
